import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';

const PERIODS = [
  { key: 'day', label: 'Diário', desc: 'últimos 30 dias' },
  { key: 'week', label: 'Semanal', desc: 'últimas 30 semanas' },
  { key: 'month', label: 'Mensal', desc: 'últimos 30 meses' },
];

const PERIOD_LABEL = {
  day: (d) => new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
  week: (d) => {
    const dt = new Date(d + 'T00:00:00');
    return `${dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`;
  },
  month: (d) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  },
};

export function SalesReportPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('day');
  const [businessId, setBusinessId] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carrega comércios aprovados para o filtro
  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get('/admin/businesses?status=approved&limit=200');
        setBusinesses(res.data || []);
      } catch (_) {}
    })();
  }, []);

  const fetchReport = useCallback(async (p, bizId) => {
    setLoading(true);
    setError(null);
    try {
      let url = `/payments/admin/reports?period=${p}&limit=30`;
      if (bizId) url += `&business_id=${bizId}`;
      const { data: res } = await api.get(url);
      setData(res.data || []);
      setSummary(res.summary || { total_credits: 0, total_tx: 0 });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(period, businessId); }, [period, businessId, fetchReport]);

  const handleDownloadCSV = () => {
    if (data.length === 0) return;
    const periodLabel = PERIODS.find(p => p.key === period)?.label || period;
    const header = 'Período,Créditos vendidos,Vendas,Ticket médio (cr)';
    const rows = data.map(r => {
      const label = PERIOD_LABEL[period](r.period_start);
      const avg = r.tx_count > 0 ? (r.credits_sold / r.tx_count).toFixed(1) : '0';
      return `"${label}",${r.credits_sold},${r.tx_count},${avg}`;
    });
    const csv = [header, ...rows].join('\n');
    const bom = '\uFEFF';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendas-${period}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const gradientBg = 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)';

  // Estima receita baseado nos planos (média ~R$ 0,10/crédito)
  const estimatedRevenue = summary ? (summary.total_credits * 0.10).toFixed(2) : '0.00';

  const maxCredits = Math.max(1, ...data.map(d => d.credits_sold));

  return (
    <div className="nc-page" style={{ paddingBottom: 40 }}>
      <div style={{ background: gradientBg, color: 'var(--nc-on-primary)', padding: '18px 16px 22px', borderRadius: '0 0 22px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate('/admin')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Relatório de vendas</h1>
        </div>

        {/* Business filter */}
        <div style={{ marginBottom: 0 }}>
          <select
            value={businessId}
            onChange={e => setBusinessId(e.target.value)}
            aria-label="Filtrar por comércio"
            style={{
              width: '100%', padding: '9px 12px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)',
              color: 'var(--nc-on-primary)', fontSize: 13, outline: 'none', marginTop: 10,
            }}
          >
            <option value="" style={{ color: 'var(--nc-text)' }}>🏪 Todos os comércios</option>
            {businesses.map(b => (
              <option key={b.id} value={b.id} style={{ color: 'var(--nc-text)' }}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {/* Period tabs */}
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              style={{
                flex: 1, padding: '9px 6px', borderRadius: 10, border: 'none',
                background: period === p.key ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
                color: 'var(--nc-on-primary)', fontSize: 13, fontWeight: period === p.key ? 700 : 500,
                cursor: 'pointer', transition: 'background 0.15s',
              }}
            >
              <div>{p.label}</div>
              <div style={{ fontSize: 10, opacity: 0.7, marginTop: 1 }}>{p.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {loading ? <Loading message="Carregando relatório..." /> : error ? (
          <div role="alert" style={{ padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)', color: 'var(--nc-danger-text)', fontSize: 13 }}>
            {error}
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 20 }}>💰</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--nc-boost)', marginTop: 4 }}>
                  {summary.total_credits.toLocaleString()}
                </div>
                <div style={{ fontSize: 10, color: 'var(--nc-text-secondary)', marginTop: 2 }}>créditos vendidos</div>
              </div>
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 20 }}>📦</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--nc-primary)', marginTop: 4 }}>
                  {summary.total_tx}
                </div>
                <div style={{ fontSize: 10, color: 'var(--nc-text-secondary)', marginTop: 2 }}>vendas realizadas</div>
              </div>
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 20 }}>💵</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--nc-success-text)', marginTop: 4 }}>
                  R$ {estimatedRevenue}
                </div>
                <div style={{ fontSize: 10, color: 'var(--nc-text-secondary)', marginTop: 2 }}>receita estimada</div>
              </div>
            </div>

            {/* Bar chart */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-strong)' }}>
                Créditos vendidos por {PERIODS.find(p => p.key === period)?.label.toLowerCase()}
              </div>
              <button
                onClick={handleDownloadCSV}
                style={{
                  padding: '6px 14px', borderRadius: 8, border: '1px solid var(--nc-boost)',
                  background: 'var(--nc-surface)', color: 'var(--nc-boost)', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Baixar CSV
              </button>
            </div>
            {data.length === 0 ? (
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--nc-text-muted)' }}>
                Nenhuma venda neste período.
              </div>
            ) : (
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.map((row, i) => {
                    const pct = (row.credits_sold / maxCredits) * 100;
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 65, fontSize: 11, fontWeight: 600, color: 'var(--nc-text-secondary)', textAlign: 'right', flexShrink: 0 }}>
                          {PERIOD_LABEL[period](row.period_start)}
                        </div>
                        <div style={{ flex: 1, height: 22, background: 'var(--nc-surface-alt)', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                          <div style={{
                            position: 'absolute', top: 0, left: 0, height: '100%',
                            width: `${pct}%`, minWidth: row.credits_sold > 0 ? 4 : 0,
                            background: 'linear-gradient(90deg, var(--nc-boost), #a78bfa)',
                            borderRadius: 6, transition: 'width 0.4s ease',
                          }} />
                        </div>
                        <div style={{ width: 55, fontSize: 12, fontWeight: 700, color: 'var(--nc-boost)', flexShrink: 0 }}>
                          {row.credits_sold}
                        </div>
                        <div style={{ width: 36, fontSize: 10, color: 'var(--nc-text-muted)', flexShrink: 0 }}>
                          ({row.tx_count})
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Legenda */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 10, color: 'var(--nc-text-muted)' }}>
                  <span>Barra = créditos vendidos</span>
                  <span>(tx) = nº de vendas</span>
                </div>
              </div>
            )}

            {/* Revenue note */}
            <div style={{
              marginTop: 16, padding: 12, borderRadius: 10,
              background: '#f5f3ff', border: '1px solid #ddd6fe',
              fontSize: 12, color: '#4c1d95', lineHeight: 1.6
            }}>
              <strong>📊 Sobre a receita:</strong> O valor exato em R$ depende do plano escolhido por cada comprador. A estimativa usa R$ 0,10/crédito (média dos planos disponíveis). Para valores exatos, consulte o extrato de cada transação.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
